import React from 'react';
import { Box, Typography } from '@mui/material';
import { MeetingRoom as LobbyIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import type { LobbyTrackActivity } from '../../Types/Incident';
import { getLobbyTrackActivityDisplay } from '../../Utils/lobbyTrackDisplay';
import LobbyTrackVisitChip from './LobbyTrackVisitChip';

interface LobbyTrackContextLineProps {
  activity?: LobbyTrackActivity | null;
  /** Show the visit status chip inline */
  showChip?: boolean;
}

const LobbyTrackContextLine: React.FC<LobbyTrackContextLineProps> = ({
  activity,
  showChip = false,
}) => {
  const display = getLobbyTrackActivityDisplay(activity);
  if (!display.hasLobbyTrack) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {/* Primary line: icon + text + chip */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
        role="status"
        aria-label={[display.primaryText, display.chipLabel].filter(Boolean).join(' — ')}
      >
        <LobbyIcon
          sx={{ fontSize: 15, color: appColors.primary.accent, flexShrink: 0 }}
        />
        {display.primaryText && (
          <Typography
            variant="caption"
            sx={{
              color: appColors.text.heading,
              fontSize: '0.72rem',
              fontWeight: 600,
            }}
          >
            {display.primaryText}
          </Typography>
        )}
        {showChip && display.chipLabel && display.chipTone && (
          <LobbyTrackVisitChip label={display.chipLabel} tone={display.chipTone} />
        )}
      </Box>

      {/* Secondary line: no-sign-out / host info */}
      {display.secondaryText && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2.25 }}>
          <Typography
            variant="caption"
            sx={{
              color: appColors.text.caption,
              fontSize: '0.68rem',
              fontWeight: 500,
              fontStyle: 'italic',
            }}
          >
            {display.secondaryText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(LobbyTrackContextLine);
